import { useState, useEffect, useCallback } from 'react';
import { apiHolder, apis } from 'api';
const CONTACT_TYPES = ['enterprise', 'personal', 'external', 'customer', 'clue', 'openSea'];
const ORG_TYPES = ['0', '1', '2', '99', '2000', '2001', '2002'];
const ORG_CONTACT_TYPES = ['enterprise', 'personal'];
const DEXIE_DB_NAME = 'contact_dexie';
const ALL_TABLE_NAMES = ['contact', 'contactItem', 'org', 'orgContact', 'personal_mark', 'frequentContact', 'recent_contact', 'lx_info'];
export function formatBytes(bytes) {
    if (bytes >= 1048576)
        return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
}
export function toRelTime(ts) {
    if (ts === null)
        return '从未';
    const diff = Date.now() - ts;
    if (diff < 0)
        return '刚刚';
    if (diff < 60000)
        return '刚刚';
    if (diff < 3600000)
        return `${Math.floor(diff / 60000)}min ago`;
    if (diff < 86400000)
        return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
}
export function fetchAllAccounts() {
    try {
        const accountApi = apiHolder.api.requireLogicalApi(apis.accountApiImpl);
        const systemApi = apiHolder.api.getSystemApi();
        const subAccounts = accountApi.getLocalSubAccountsFromCache({ expired: false });
        return subAccounts.map(account => {
            var _a;
            return ({
                id: account.id,
                email: account.id,
                nickName: account.nickName,
                accountMd5: ((_a = systemApi.getCurrentUser(account.id)) === null || _a === void 0 ? void 0 : _a.accountMd5) || '',
            });
        });
    }
    catch (_a) {
        return [];
    }
}
export function fetchEnterpriseConfig(_account) {
    var _a;
    try {
        const contactApi = apiHolder.api.requireLogicalApi(apis.contactApiImpl);
        const contactApiFn = contactApi;
        if (typeof contactApiFn.getEnterpriseConfig === 'function') {
            return (_a = contactApiFn.getEnterpriseConfig(_account)) !== null && _a !== void 0 ? _a : null;
        }
        return null;
    }
    catch (_b) {
        return null;
    }
}
export async function fetchPersonalMarkStats(dexieApi, accountMd5) {
    try {
        const [contactStarResults, groupStarResults] = await Promise.all([
            dexieApi.getByRangeCondition({
                dbName: DEXIE_DB_NAME,
                tableName: 'personal_mark',
                adCondition: { type: 'equals', field: 'type', args: [1] },
            }, accountMd5),
            dexieApi.getByRangeCondition({
                dbName: DEXIE_DB_NAME,
                tableName: 'personal_mark',
                adCondition: { type: 'equals', field: 'type', args: [2] },
            }, accountMd5),
        ]);
        return {
            contactStars: Array.isArray(contactStarResults) ? contactStarResults.length : 0,
            groupStars: Array.isArray(groupStarResults) ? groupStarResults.length : 0,
        };
    }
    catch (_a) {
        return { contactStars: 0, groupStars: 0 };
    }
}
export async function countByType(dexieApi, tableName, types, accountMd5) {
    const breakdown = {};
    const results = await Promise.all(types.map(async (type) => {
        try {
            const dexieApiFn = dexieApi;
            if (typeof dexieApiFn.getByEqConditionWithTotal === 'function') {
                const res = await dexieApiFn.getByEqConditionWithTotal({
                    dbName: DEXIE_DB_NAME,
                    tableName,
                    query: { type },
                    _dbAccount: accountMd5,
                });
                return { type, count: res.totalCount };
            }
            const res = await dexieApi.getByEqCondition({ dbName: DEXIE_DB_NAME, tableName, query: { type }, _dbAccount: accountMd5 });
            return { type, count: Array.isArray(res) ? res.length : 0 };
        }
        catch (_a) {
            return { type, count: 0 };
        }
    }));
    results.forEach(r => {
        breakdown[r.type] = r.count;
    });
    return breakdown;
}
export async function fetchDexieStats(dexieApi, accountMd5) {
    const totals = await Promise.all(ALL_TABLE_NAMES.map(tableName => dexieApi.getTableCount({ dbName: DEXIE_DB_NAME, tableName }, accountMd5).catch(() => 0)));
    const result = ALL_TABLE_NAMES.map((tableName, i) => ({
        tableName,
        total: totals[i],
    }));
    const [contactBreakdown, orgBreakdown, orgContactBreakdown] = await Promise.all([
        countByType(dexieApi, 'contact', CONTACT_TYPES, accountMd5),
        countByType(dexieApi, 'org', ORG_TYPES, accountMd5),
        countByType(dexieApi, 'orgContact', ORG_CONTACT_TYPES, accountMd5),
    ]);
    const contactStat = result.find(t => t.tableName === 'contact');
    if (contactStat)
        contactStat.breakdown = contactBreakdown;
    const orgStat = result.find(t => t.tableName === 'org');
    if (orgStat)
        orgStat.breakdown = orgBreakdown;
    const orgContactStat = result.find(t => t.tableName === 'orgContact');
    if (orgContactStat)
        orgContactStat.breakdown = orgContactBreakdown;
    return result;
}
export async function fetchLokiStats(lokiApi, _account) {
    var _a;
    try {
        if (lokiApi && typeof lokiApi === 'object' && typeof lokiApi.getContactSearchStats === 'function') {
            const stats = await lokiApi.getContactSearchStats(_account);
            return {
                totalSizeBytes: (_a = stats.totalSizeBytes) !== null && _a !== void 0 ? _a : 0,
                tables: Array.isArray(stats.tables) ? stats.tables : [],
            };
        }
        return { totalSizeBytes: 0, tables: [] };
    }
    catch (_b) {
        return { totalSizeBytes: 0, tables: [] };
    }
}
function fetchTrackerSnapshot(_account) {
    try {
        const contactApi = apiHolder.api.requireLogicalApi(apis.contactApiImpl);
        const contactApiFn = contactApi;
        if (typeof contactApiFn.getSyncTracker !== 'function')
            return null;
        const tracker = contactApiFn.getSyncTracker(_account);
        if (tracker && typeof tracker.getSnapshot === 'function') {
            return tracker.getSnapshot();
        }
        return null;
    }
    catch (_a) {
        return null;
    }
}
async function fetchSnapshotForAccount(accountId) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const systemApi = apiHolder.api.getSystemApi();
    const dexieApi = apiHolder.api.requireLogicalApi(apis.dexieDbApi);
    const lokiApi = apiHolder.api.requireLogicalApi(apis.dbMemoryApiImpl);
    const user = systemApi.getCurrentUser(accountId);
    const accountMd5 = (_a = user === null || user === void 0 ? void 0 : user.accountMd5) !== null && _a !== void 0 ? _a : '';
    const trackerSnapshot = fetchTrackerSnapshot(accountId);
    const enterpriseConfig = fetchEnterpriseConfig(accountId);
    const [personalMarkStats, dexieTables, lokiStats] = await Promise.all([
        fetchPersonalMarkStats(dexieApi, accountMd5),
        fetchDexieStats(dexieApi, accountMd5),
        fetchLokiStats(lokiApi, accountId),
    ]);
    const dexieContactTotal = (_c = (_b = dexieTables.find(t => t.tableName === 'contact')) === null || _b === void 0 ? void 0 : _b.total) !== null && _c !== void 0 ? _c : 0;
    const dexieOrgTotal = (_e = (_d = dexieTables.find(t => t.tableName === 'org')) === null || _d === void 0 ? void 0 : _d.total) !== null && _e !== void 0 ? _e : 0;
    const lokiContactCount = (_g = (_f = lokiStats.tables.find(t => t.name === 'contact')) === null || _f === void 0 ? void 0 : _f.count) !== null && _g !== void 0 ? _g : 0;
    const lokiOrgCount = (_j = (_h = lokiStats.tables.find(t => t.name === 'org')) === null || _h === void 0 ? void 0 : _h.count) !== null && _j !== void 0 ? _j : 0;
    return {
        trackerSnapshot,
        enterpriseConfig,
        personalMarkStats,
        dexieTables,
        lokiStats,
        consistency: {
            contactMatch: dexieContactTotal === lokiContactCount,
            contactDiff: Math.abs(dexieContactTotal - lokiContactCount),
            orgMatch: dexieOrgTotal === lokiOrgCount,
            orgDiff: Math.abs(dexieOrgTotal - lokiOrgCount),
        },
    };
}
export function useDebugData(visible) {
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        var _a, _b;
        if (!visible)
            return;
        const systemApi = apiHolder.api.getSystemApi();
        const currentUser = systemApi.getCurrentUser();
        const currentId = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) !== null && _a !== void 0 ? _a : '';
        setSelectedAccount(prev => prev || currentId);
        const fetchedAccounts = fetchAllAccounts();
        const hasCurrentUser = fetchedAccounts.some(a => a.id === currentId);
        if (!hasCurrentUser && currentId) {
            fetchedAccounts.unshift({
                id: currentId,
                email: currentId,
                nickName: currentUser === null || currentUser === void 0 ? void 0 : currentUser.nickName,
                accountMd5: (_b = currentUser === null || currentUser === void 0 ? void 0 : currentUser.accountMd5) !== null && _b !== void 0 ? _b : '',
            });
        }
        setAccounts(fetchedAccounts);
    }, [visible]);
    const refresh = useCallback(() => {
        if (!selectedAccount)
            return;
        setLoading(true);
        setError(null);
        fetchSnapshotForAccount(selectedAccount)
            .then(setSnapshot)
            .catch(e => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setLoading(false));
    }, [selectedAccount]);
    useEffect(() => {
        if (visible && selectedAccount) {
            refresh();
        }
    }, [visible, selectedAccount, refresh]);
    return { accounts, selectedAccount, setSelectedAccount, snapshot, loading, error, refresh };
}
